import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Eye, Heart, Edit3, Trash2, MessageCircle, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/app/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { marketApi } from "@/lib/marketApi";
import { fmtMoney } from "@/lib/format";
import ListingComposer from "@/components/marketplace/ListingComposer";

export default function ListingDetail() {
  const { lid } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [item, setItem] = useState(null);
  const [cats, setCats] = useState([]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactMsg, setContactMsg] = useState("Hola, ¿sigue disponible?");
  const [favorited, setFavorited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    marketApi.detail(lid).then(setItem).catch(() => toast.error("Anuncio no encontrado"));
    marketApi.categories().then(setCats);
  }, [lid]);

  useEffect(() => {
    if (!auth?.token || !item) return;
    marketApi.favoriteStatus(auth.token, item.id)
      .then((r) => setFavorited(!!r.favorited))
      .catch(() => {});
  }, [auth?.token, item]);

  if (!item) {
    return <AppLayout hideRightRail><div className="p-12 text-center text-ink-mid">Cargando…</div></AppLayout>;
  }

  const isOwner = auth?.user?.id === item.seller_id;
  const photos = item.photos?.length ? item.photos : [];
  const cover = photos[photoIdx];
  const catLabel = cats.find((c) => c.id === item.category)?.label || item.category;
  const condLabel = { new: "Nuevo", like_new: "Como nuevo", used: "Usado", refurbished: "Reacondicionado", n_a: "Servicio" }[item.condition] || "";

  const handleContact = async (e) => {
    e?.preventDefault();
    if (!auth?.user) {
      toast.error("Inicia sesión para contactar");
      return;
    }
    setContactBusy(true);
    try {
      const data = await marketApi.contactSeller(auth.token, item.id, contactMsg.trim() || "Hola, ¿sigue disponible?");
      toast.success("Mensaje enviado");
      navigate(`/marketplace/chat/${data.thread.id}`);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Error al contactar");
    } finally {
      setContactBusy(false);
    }
  };

  const handleFav = async () => {
    if (!auth?.user) { toast.error("Inicia sesión para guardar"); return; }
    setFavLoading(true);
    try {
      const r = await marketApi.toggleFavorite(auth.token, item.id);
      setFavorited(r.favorited);
      toast.success(r.favorited ? "Guardado en favoritos" : "Quitado de favoritos");
    } catch { toast.error("Error"); } finally { setFavLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar este anuncio? Esta acción no se puede deshacer.")) return;
    try {
      await marketApi.remove(auth.token, item.id);
      toast.success("Anuncio eliminado");
      navigate("/marketplace");
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Error"); }
  };

  return (
    <AppLayout hideRightRail>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-ink-mid hover:text-coral mb-4" data-testid="detail-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver
      </button>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Photo gallery */}
        <div className="lg:col-span-7">
          <div data-testid="detail-cover" className="aspect-[4/3] rounded-2xl overflow-hidden bg-cream-deep relative">
            {cover ? (
              <img src={cover} alt={item.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-mono text-sm text-ink-mid">Sin imagen</div>
            )}
            {photos.length > 1 && (
              <>
                <button onClick={() => setPhotoIdx((photoIdx - 1 + photos.length) % photos.length)} data-testid="detail-prev"
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-cream/90 rounded-full border hairline hover:bg-cream">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPhotoIdx((photoIdx + 1) % photos.length)} data-testid="detail-next"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-cream/90 rounded-full border hairline hover:bg-cream">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <span className="absolute bottom-2 right-2 bg-ink/80 text-cream text-[11px] font-mono px-2 py-0.5 rounded-full">
                  {photoIdx + 1} / {photos.length}
                </span>
              </>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setPhotoIdx(i)} data-testid={`detail-thumb-${i}`}
                        className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${i === photoIdx ? "border-coral" : "border-transparent hairline border"}`}>
                  <img src={p} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral mb-1">
            {catLabel} · {item.type === "service" ? "Servicio" : "Producto"}{condLabel ? ` · ${condLabel}` : ""}
            {item.status === "sold" && <span className="ml-2 bg-zinc-900 text-white px-2 py-0.5 rounded-full">Vendido</span>}
            {item.status === "paused" && <span className="ml-2 bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">Pausado</span>}
          </p>
          <h1 data-testid="detail-title" className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mb-2">{item.title}</h1>
          <p data-testid="detail-price" className="font-serif text-4xl text-coral mb-4">{fmtMoney(item.price_usd)} <span className="text-base text-ink-mid font-mono">{item.currency || "USD"}</span></p>

          <div className="flex items-center gap-3 text-xs text-ink-mid mb-5">
            {item.location && (
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.location}</span>
            )}
            <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {item.views} vistas</span>
            <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> {item.saved_count}</span>
          </div>

          {item.description && (
            <div className="mb-5 prose-sm">
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{item.description}</p>
            </div>
          )}

          <div className="border hairline rounded-2xl p-4 bg-cream-card mb-4">
            <p className="font-mono text-[10px] uppercase tracking-wider text-ink-mid mb-2">Vendedor</p>
            <div className="flex items-center gap-3">
              {item.seller_avatar
                ? <img src={item.seller_avatar} alt="" className="w-10 h-10 rounded-full" />
                : <div className="w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center font-serif text-coral">
                    {(item.seller_name || "?")[0]}
                  </div>}
              <p className="font-medium text-sm">{item.seller_name || "—"}</p>
            </div>
          </div>

          {/* Actions */}
          {isOwner ? (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setEditOpen(true)} data-testid="detail-edit-btn"
                      className="inline-flex items-center justify-center gap-1.5 border hairline rounded-full py-2.5 text-sm hover:border-coral transition">
                <Edit3 className="w-3.5 h-3.5" /> Editar
              </button>
              <button onClick={handleDelete} data-testid="detail-delete-btn"
                      className="inline-flex items-center justify-center gap-1.5 border border-red-300 text-red-700 rounded-full py-2.5 text-sm hover:bg-red-50 transition">
                <Trash2 className="w-3.5 h-3.5" /> Eliminar
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {!contactOpen ? (
                <button onClick={() => setContactOpen(true)} disabled={item.status === "sold"} data-testid="detail-contact-btn"
                        className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full py-3 text-sm hover:bg-coral-hover disabled:opacity-50 transition">
                  <MessageCircle className="w-4 h-4" /> {item.status === "sold" ? "Vendido" : "Contactar al vendedor"}
                </button>
              ) : (
                <form onSubmit={handleContact} className="space-y-2">
                  <textarea value={contactMsg} onChange={(e) => setContactMsg(e.target.value)} rows={3}
                            data-testid="detail-contact-msg" maxLength={2000}
                            className="w-full bg-cream border hairline rounded-xl px-3 py-2 text-sm outline-none focus:border-coral resize-y" />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setContactOpen(false)}
                            className="flex-1 border hairline rounded-full py-2 text-sm hover:border-coral">
                      Cancelar
                    </button>
                    <button type="submit" disabled={contactBusy} data-testid="detail-contact-submit"
                            className="flex-[2] inline-flex items-center justify-center gap-1.5 bg-coral text-white rounded-full py-2 text-sm hover:bg-coral-hover disabled:opacity-50">
                      {contactBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
                      Enviar mensaje
                    </button>
                  </div>
                </form>
              )}
              <button onClick={handleFav} disabled={favLoading} data-testid="detail-fav-btn"
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm transition border hairline
                        ${favorited ? "bg-coral text-white border-coral" : "hover:border-coral"}`}>
                <Heart className={`w-4 h-4 ${favorited ? "fill-current" : ""}`} />
                {favorited ? "Guardado" : "Guardar"}
              </button>
            </div>
          )}

          {item.contact && !isOwner && (
            <p className="mt-3 text-[11px] text-ink-mid font-mono">Contacto del vendedor: <span className="text-coral">{item.contact}</span></p>
          )}
        </div>
      </div>

      {editOpen && (
        <ListingComposer cats={cats} initial={item} onClose={() => setEditOpen(false)}
                         onCreated={(updated) => { setItem(updated); setEditOpen(false); }} />
      )}
    </AppLayout>
  );
}
